/**
 * LDT-only dialog to collect Specimen & Patient Information for slide image upload.
 * Validates patient name and DOB against the order's patient before allowing submit.
 * Layout matches order forms: two-column rows (ResponsiveFormRow) and SectionHeader for sections.
 */
import { FC, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  Typography,
  Stack,
  useTheme,
} from '@mui/material';
import { isSameDay } from 'date-fns';
import { z } from 'zod';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { BLOCK_ID_OPTIONS } from '@ldt/features/orders/utils/slide_site_options';
import { FormDatePicker } from 'src/components/date_picker';
import { SectionHeader } from 'src/components/section_header';
import { SelectList, SelectOption } from 'src/components/select_list';
import { getOrderPatientFullName } from 'src/distribution_platforms/features/utils/patient_utils';
import {
  BlockId,
  LdtOrderDetailFieldsFragment,
  SlideMetadataInputType,
  SlideSite,
} from 'src/types';
import { parseIsoDateString } from 'src/utils/date_parsing';

export type SlideSiteSelectOption = { value: SlideSite; label: string };

export interface SlideImageMetadataDialogProps {
  open: boolean;
  onClose: () => void;
  order: LdtOrderDetailFieldsFragment;
  selectedFile: File;
  specimenTypeLabel: string;
  slideSiteOptions: SlideSiteSelectOption[];
  onSubmit: (metadata: SlideMetadataInputType) => void;
}

const slideMetadataFormSchema = z
  .object({
    specimenId: z.string().min(1, 'Specimen ID is required'),
    accession: z.string().min(1, 'Accession ID is required'),
    blockId: z
      .union([z.enum(BlockId), z.literal('')])
      .refine((v) => v !== '', { message: 'Block is required' }),
    specimenSite: z
      .union([z.enum(SlideSite), z.literal('')])
      .refine((v) => v !== '', { message: 'Region is required' }),
    specimenSiteLabel: z.string(),
    patientName: z.string().min(1, 'Patient Name is required'),
    dateOfBirth: z.string().min(1, 'Date of Birth is required'),
  })
  .refine(
    (data) =>
      data.specimenSite !== SlideSite.Other ||
      data.specimenSiteLabel.trim().length > 0,
    {
      message: 'Specimen site label is required when Region is Other',
      path: ['specimenSiteLabel'],
    },
  );

/** Form values: blockId/specimenSite allow '' before user selects; schema refines to require them. */
export type SlideMetadataFormData = {
  specimenId: string;
  accession: string;
  blockId: '' | BlockId;
  specimenSite: '' | SlideSite;
  specimenSiteLabel: string;
  patientName: string;
  dateOfBirth: string;
};

const defaultValues: SlideMetadataFormData = {
  specimenId: '',
  accession: '',
  blockId: '',
  specimenSite: '',
  specimenSiteLabel: '',
  patientName: '',
  dateOfBirth: '',
};

export const SlideImageMetadataDialog: FC<SlideImageMetadataDialogProps> = ({
  open,
  onClose,
  order,
  selectedFile: _selectedFile,
  specimenTypeLabel,
  slideSiteOptions,
  onSubmit,
}) => {
  const theme = useTheme();
  const orderPatientFullName = getOrderPatientFullName(order);
  const orderDobDate = parseIsoDateString(
    order.patient?.dateOfBirthPhi ?? null,
  );

  // Post-RP specimen type has only one slide site option, so default to that.
  const formDefaultValues = useMemo<SlideMetadataFormData>(
    () => ({
      ...defaultValues,
      specimenSite:
        slideSiteOptions.length === 1 ? slideSiteOptions[0].value : '',
    }),
    [slideSiteOptions],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    watch,
  } = useForm<SlideMetadataFormData>({
    resolver: zodResolver(slideMetadataFormSchema),
    defaultValues: formDefaultValues,
    mode: 'onBlur',
  });

  const watched = watch();
  const specimenSite = watched.specimenSite;
  const isRequiredFilled =
    !!watched.specimenId?.trim() &&
    !!watched.accession?.trim() &&
    !!watched.blockId &&
    !!watched.specimenSite &&
    (watched.specimenSite !== SlideSite.Other ||
      !!watched.specimenSiteLabel?.trim()) &&
    !!watched.patientName?.trim() &&
    !!watched.dateOfBirth?.trim();

  useEffect(() => {
    if (open) {
      reset(formDefaultValues);
    }
  }, [formDefaultValues, open, reset]);

  const onValidSubmit = (data: SlideMetadataFormData) => {
    // Validate patient name matches order
    const expectedName = orderPatientFullName?.trim().toLowerCase();
    const enteredName = data.patientName.trim().toLowerCase();
    if (!expectedName) {
      setError('patientName', {
        type: 'manual',
        message: 'Patient info not available',
      });
      return;
    }
    if (expectedName !== enteredName) {
      setError('patientName', {
        type: 'manual',
        message: 'Patient Name does not match.',
      });
      return;
    }

    // Validate DOB matches order
    if (!orderDobDate) {
      setError('dateOfBirth', {
        type: 'manual',
        message: order.patient?.dateOfBirthPhi
          ? 'Date of Birth is required'
          : 'Patient info not available',
      });
      return;
    }
    const enteredDob = parseIsoDateString(data.dateOfBirth);
    if (!enteredDob) {
      setError('dateOfBirth', {
        type: 'manual',
        message: 'Date of Birth is required',
      });
      return;
    }
    if (!isSameDay(enteredDob, orderDobDate)) {
      setError('dateOfBirth', {
        type: 'manual',
        message: 'Date of Birth does not match.',
      });
      return;
    }

    const metadata: SlideMetadataInputType = {
      specimenId: data.specimenId.trim(),
      accession: data.accession.trim(),
      specimenSite: data.specimenSite as SlideSite,
      specimenSiteLabel:
        data.specimenSite === SlideSite.Other
          ? data.specimenSiteLabel.trim() || null
          : null,
      blockId: data.blockId as BlockId,
    };
    onSubmit(metadata);
    reset(formDefaultValues);
    onClose();
  };

  const handleClose = () => {
    reset(formDefaultValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ p: 0, textAlign: 'left' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
            }}
          >
            Specimen & Patient Information
          </Typography>
          <Chip
            label={
              <Typography variant='body2'>
                {`Specimen Type: `}
                <Typography
                  component='span'
                  variant='body2'
                  color='primary'
                  sx={{
                    fontWeight: theme.typography.fontWeightMedium,
                  }}
                >
                  {specimenTypeLabel}
                </Typography>
              </Typography>
            }
            sx={{
              backgroundColor: theme.palette.action.hover,
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Stack
          component='form'
          spacing={2.5}
          sx={{ mt: 2 }}
          noValidate
          onSubmit={handleSubmit(onValidSubmit)}
          id='slide-metadata-form'
        >
          <Box>
            <SectionHeader title='Specimen Information' />
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              <ResponsiveFormRow>
                <Box
                  data-testid='specimen-id-field'
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='specimenId'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Specimen ID'
                        required
                        fullWidth
                        error={!!errors.specimenId}
                        helperText={errors.specimenId?.message}
                      />
                    )}
                  />
                </Box>
                <Box
                  data-testid='accession-id-field'
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='accession'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Accession ID'
                        required
                        fullWidth
                        error={!!errors.accession}
                        helperText={errors.accession?.message}
                      />
                    )}
                  />
                </Box>
              </ResponsiveFormRow>
              <ResponsiveFormRow>
                <Box
                  data-testid='block-select'
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='blockId'
                    control={control}
                    render={({ field }) => (
                      <SelectList
                        label='Block*'
                        value={field.value}
                        options={BLOCK_ID_OPTIONS.map(
                          (opt): SelectOption => ({
                            value: opt.value,
                            name: opt.label,
                          }),
                        )}
                        onChange={(e) =>
                          field.onChange(e.target.value as BlockId | '')
                        }
                        validationText={errors.blockId?.message ?? ''}
                      />
                    )}
                  />
                </Box>
                <Box
                  data-testid='region-select'
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='specimenSite'
                    control={control}
                    render={({ field }) => (
                      <SelectList
                        label='Region'
                        required
                        value={field.value}
                        options={slideSiteOptions.map(
                          (opt): SelectOption => ({
                            value: opt.value,
                            name: opt.label,
                          }),
                        )}
                        onChange={(e) =>
                          field.onChange(e.target.value as SlideSite | '')
                        }
                        validationText={errors.specimenSite?.message ?? ''}
                      />
                    )}
                  />
                </Box>
              </ResponsiveFormRow>
              {specimenSite === SlideSite.Other && (
                <Controller
                  name='specimenSiteLabel'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label='Specimen site label'
                      required
                      fullWidth
                      error={!!errors.specimenSiteLabel}
                      helperText={errors.specimenSiteLabel?.message}
                    />
                  )}
                />
              )}
            </Stack>
          </Box>

          <Box>
            <SectionHeader title='Patient Information' />
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              <ResponsiveFormRow>
                <Box
                  data-testid='patient-name-field'
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='patientName'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label='Patient Name'
                        required
                        fullWidth
                        error={!!errors.patientName}
                        helperText={errors.patientName?.message}
                        placeholder='Patient Name*'
                      />
                    )}
                  />
                </Box>
                <Box
                  data-testid='date-of-birth-field'
                  sx={{
                    flex: 1,
                  }}
                >
                  <Controller
                    name='dateOfBirth'
                    control={control}
                    render={({ field }) => (
                      <FormDatePicker
                        label='Date of Birth'
                        maxDate={new Date()}
                        value={field.value || null}
                        required
                        fullWidth
                        error={!!errors.dateOfBirth}
                        helperText={errors.dateOfBirth?.message ?? ' '}
                        onChange={(value) => field.onChange(value ?? '')}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                </Box>
              </ResponsiveFormRow>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 0, pb: 0, pt: 2 }}>
        <Button onClick={handleClose}>CLOSE</Button>
        <Button
          variant='contained'
          type='submit'
          form='slide-metadata-form'
          disabled={!isRequiredFilled}
        >
          SUBMIT
        </Button>
      </DialogActions>
    </Dialog>
  );
};
